import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import { usePagesTranslation } from "../locales";

/** Green icon that opens a dialog with the reference image (same layout as jigsaw play). */
export default function IconImagePreview({
    imageUrl,
    sx,
    tooltip,
    dialogTitle,
    compact = false,
    hideTitle = false,
    previewSize = 200,
}) {
    const { t } = usePagesTranslation();
    const [open, setOpen] = useState(false);

    if (!imageUrl) return null;

    const label =
        tooltip ?? t("puzzle.jigsaw.view-full-image", "View image");
    const title = dialogTitle ?? label;
    const size = compact ? previewSize : null;
    const showTitle = !hideTitle && compact;

    return (
        <>
            <Tooltip title={label} arrow>
                <IconButton
                    size="small"
                    onClick={() => setOpen(true)}
                    aria-label={label}
                    sx={{ color: "#3BA666", ...sx }}
                >
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth={compact ? false : "lg"}
                fullWidth={!compact}
                PaperProps={
                    compact
                        ? {
                              sx: {
                                  width: "auto",
                                  maxWidth: "min(100vw - 32px, 280px)",
                              },
                          }
                        : undefined
                }
            >
                <DialogContent
                    sx={{
                        p: compact ? 1.25 : 1.5,
                        position: "relative",
                        ...(compact ? { width: size + 24 } : {}),
                    }}
                >
                    {showTitle ? (
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 0.75, pr: 4 }}
                        >
                            {title}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            position: "absolute",
                            top: compact ? 4 : 8,
                            right: compact ? 4 : 8,
                            zIndex: 1,
                        }}
                    >
                        <IconButton
                            onClick={() => setOpen(false)}
                            size={compact ? "small" : "medium"}
                            sx={{
                                bgcolor: "background.paper",
                                boxShadow: 1,
                                "&:hover": { bgcolor: "background.paper" },
                            }}
                            aria-label="Close"
                        >
                            <CloseIcon fontSize={compact ? "small" : "medium"} />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src={imageUrl}
                            alt=""
                            sx={{
                                width: compact ? size : "100%",
                                height: compact ? size : "auto",
                                maxWidth: "100%",
                                maxHeight: compact ? size : "85vh",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}

IconImagePreview.propTypes = {
    imageUrl: PropTypes.string,
    sx: PropTypes.object,
    tooltip: PropTypes.string,
    dialogTitle: PropTypes.string,
    compact: PropTypes.bool,
    hideTitle: PropTypes.bool,
    previewSize: PropTypes.number,
};
